import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import LandingPage from './components/LandingPage';
import LandingV2A from './components/LandingV2A';
import LandingV2B from './components/LandingV2B';
import LandingV2C from './components/LandingV2C';
import LandingV2D from './components/LandingV2D';
import VariantSwitcher from './components/VariantSwitcher';
import StandaloneTour from './components/StandaloneTour';
import { TermsOfService, PrivacyPolicy } from './components/LegalPages';
import { VendorPortalPage, BrandPortalPage } from './components/Portals';
import VendorPortalV2 from './components/VendorPortalV2';
import { BrandPortalApp, MgmtPortalApp } from './components/BrandMgmtPortal';
import SupportPage from './components/SupportPage';
import SignupFlow from './components/SignupFlow';
import KnowledgeBasePortal from './components/KnowledgeBasePortal';
import PreviewSite from './components/PreviewSite';
import BlogPage from './components/BlogPage';
import SnapshotViewer from './components/SnapshotViewer';
import { Toaster } from './components/ui/sonner';
import { I18nProvider } from './i18n';
import { RowDensityProvider } from './components/common/RowDensityToggle';
import { FocusModeProvider } from './components/common/FocusMode';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function App() {
  const [token, setToken] = useState(localStorage.getItem('lodgiflow_token'));
  const [role, setRole] = useState(localStorage.getItem('lodgiflow_role'));
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('lodgiflow_theme') === 'dark');
  const [page, setPageRaw] = useState('landing');
  const [tenantSlug, setTenantSlug] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [snapshotSlug, setSnapshotSlug] = useState(null);

  // Wrap setPage to also update browser URL
  const setPage = useCallback((newPage) => {
    setPageRaw(newPage);
    // Preserve existing query string when routing to app-shell pages so
    // deep-links like /app?tab=super-admin&section=marketing-cms survive
    // through the login → verify → app-mount sequence.
    const preserveSearch = window.location.search || '';
    if (newPage === 'app') {
      window.history.replaceState({}, '', `/app${preserveSearch}`);
    } else if (newPage === 'landing') {
      window.history.replaceState({}, '', '/');
    } else if (!['loading', 'auth-callback', 'tenant-login'].includes(newPage)) {
      window.history.replaceState({}, '', `/${newPage}${preserveSearch}`);
    }
  }, []);

  // Apply dark mode
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('lodgiflow_theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Service worker update detection
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(reg => {
        // Poll for updates every 2 minutes
        const interval = setInterval(() => reg.update(), 2 * 60 * 1000);
        // Check immediately if there's a waiting worker
        if (reg.waiting) {
          setSwUpdateAvailable(true);
        }
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' || newWorker.state === 'activated') {
                setSwUpdateAvailable(true);
              }
            });
          }
        });
        return () => clearInterval(interval);
      }).catch(() => {});
      // Also detect when a new SW takes control
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        setSwUpdateAvailable(true);
      });
    }
  }, []);

  // Handle browser URL for marketing pages and tenant slugs
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/app' || path.startsWith('/app/')) {
      // User is navigating directly to the app — verifyAuth will handle login
      return;
    }
    if (path === '/terms') setPageRaw('terms');
    else if (path === '/privacy') setPageRaw('privacy');
    else if (path === '/features') setPageRaw('features');
    else if (path === '/pricing') setPageRaw('pricing');
    else if (path === '/about') setPageRaw('about');
    else if (path === '/contact') setPageRaw('contact');
    else if (path === '/vendor-portal') setPageRaw('vendor-portal');
    else if (path === '/brand-portal') setPageRaw('brand-portal');
    else if (path === '/mgmt-portal') setPageRaw('mgmt-portal');
    else if (path === '/blog') setPageRaw('blog');
    else if (path.startsWith('/support/')) setPageRaw('support');
    else if (path.startsWith('/features')) setPageRaw('features');
    else if (path === '/auth/callback' || path.startsWith('/auth/callback')) setPageRaw('auth-callback');
    else if (path === '/help' || path.startsWith('/help')) setPageRaw('help');
    else if (path === '/preview' || path.startsWith('/preview')) setPageRaw('preview');
    else if (path === '/login') setPageRaw('login');
    else if (path === '/signup') setPageRaw('signup');
    else if (path === '/v2a') setPageRaw('v2a');
    else if (path === '/v2b') setPageRaw('v2b');
    else if (path === '/v2c') setPageRaw('v2c');
    else if (path === '/v2d') setPageRaw('v2d');
    else if (path === '/tour') setPageRaw('tour');
    else if (path.startsWith('/snapshot/')) {
      const slug = path.split('/snapshot/')[1]?.split('/')[0];
      if (slug) {
        setSnapshotSlug(slug);
        setPageRaw('snapshot');
      }
    }
    else if (path.startsWith('/org/')) {
      // Tenant slug URL: /org/{slug}
      const slug = path.split('/org/')[1]?.split('/')[0];
      if (slug) {
        setTenantSlug(slug);
        axios.get(`${BACKEND_URL}/api/tenant/by-slug/${slug}`)
          .then(res => { setTenantInfo(res.data); setPage('tenant-login'); })
          .catch(() => setPage('landing'));
      }
    }
  }, []);

  const verifyAuth = useCallback(async () => {
    const savedToken = localStorage.getItem('lodgiflow_token');
    if (!savedToken) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      });
      setToken(savedToken);
      setRole(res.data.role);
      // Only go to app if we're on the root page (not a marketing page)
      const path = window.location.pathname;
      const marketingPaths = ['/terms', '/privacy', '/features', '/pricing', '/about', '/contact', '/vendor-portal', '/brand-portal', '/mgmt-portal', '/support', '/auth/callback', '/org/', '/help', '/preview', '/login', '/signup', '/snapshot/', '/v2a', '/v2b', '/v2c', '/v2d', '/tour'];
      if (!marketingPaths.some(p => path.startsWith(p))) {
        setPage('app');
      }
      setLoading(false);
    } catch {
      localStorage.removeItem('lodgiflow_token');
      localStorage.removeItem('lodgiflow_role');
      setToken(null);
      setRole(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  // Handle Google OAuth callback
  useEffect(() => {
    if (page === 'auth-callback') {
      // Emergent auth returns: /auth/callback#session_id=xxx or /auth/callback?session_id=xxx
      const hash = window.location.hash;
      const search = window.location.search;
      let sessionId = null;

      // Try hash fragment first
      if (hash) {
        const hashParams = new URLSearchParams(hash.replace('#', ''));
        sessionId = hashParams.get('session_id');
      }
      // Fall back to query params
      if (!sessionId && search) {
        const queryParams = new URLSearchParams(search);
        sessionId = queryParams.get('session_id');
      }

      if (sessionId) {
        axios.post(`${BACKEND_URL}/api/auth/google/session`, { session_id: sessionId })
          .then(res => {
            handleLogin(res.data.token, res.data.role);
            window.history.replaceState({}, '', '/');
          })
          .catch((err) => {
            console.error('Google auth failed:', err.response?.data || err.message);
            setPage('login');
          });
      } else {
        console.error('No session_id found in URL:', window.location.href);
        setPage('login');
      }
    }
  }, [page]);

  const handleLogin = (newToken, newRole) => {
    localStorage.setItem('lodgiflow_token', newToken);
    localStorage.setItem('lodgiflow_role', newRole);
    setToken(newToken);
    setRole(newRole);
    setPage('app');
  };

  const handleLogout = () => {
    localStorage.removeItem('lodgiflow_token');
    localStorage.removeItem('lodgiflow_role');
    localStorage.removeItem('lodgiflow_original_token');
    localStorage.removeItem('lodgiflow_tenant_id');
    localStorage.removeItem('lodgiflow_is_super_admin');
    setToken(null);
    setRole(null);
    setPage('landing');
  };

  const goToLanding = () => {
    window.history.pushState({}, '', '/');
    setPage('landing');
  };

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
          <span className="text-sm text-muted-foreground">Loading LodgiFlow...</span>
        </div>
      </div>
    );
  }

  // Legal pages (accessible without login)
  if (page === 'terms') {
    return (
      <>
        <TermsOfService onBack={goToLanding} />
        <Toaster position="bottom-right" richColors closeButton />
      </>
    );
  }
  if (page === 'privacy') {
    return (
      <>
        <PrivacyPolicy onBack={goToLanding} />
        <Toaster position="bottom-right" richColors closeButton />
      </>
    );
  }
  if (page === 'auth-callback') {
    return (<div className="min-h-screen flex items-center justify-center"><div className="text-center"><div className="h-8 w-8 border-2 border-[#4F6DF5] border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-sm text-muted-foreground">Signing you in...</p><p className="text-[10px] text-muted-foreground mt-2">If this takes too long, <button onClick={() => setPage('login')} className="text-[#4F6DF5] underline">go to login</button></p></div></div>);
  }
  if (page === 'help') {
    return (<><KnowledgeBasePortal /><Toaster position="bottom-right" richColors closeButton /></>);
  }
  if (page === 'preview') {
    return (<><PreviewSite onNavigate={(p) => { window.history.pushState({}, '', `/${p}`); setPage(p); }} /><VariantSwitcher /><Toaster position="top-right" richColors closeButton /></>);
  }
  if (page === 'snapshot' && snapshotSlug) {
    return (
      <>
        <SnapshotViewer
          slug={snapshotSlug}
          onGoToApp={() => { window.history.pushState({}, '', '/login'); setPage('login'); }}
          onGoToLanding={goToLanding}
        />
        <Toaster position="bottom-right" richColors closeButton />
      </>
    );
  }
  if (page === 'tenant-login' && tenantInfo) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              {tenantInfo.logo_url ? (
                <img src={tenantInfo.logo_url} alt={tenantInfo.name} className="h-12 mx-auto mb-4" />
              ) : (
                <div className="flex items-center justify-center gap-2 mb-4">
                  <img src="/assets/logo.png" alt="LodgiFlow" className="h-8 w-8" />
                  <span className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>LodgiFlow</span>
                </div>
              )}
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{tenantInfo.name}</h1>
              <p className="text-sm text-muted-foreground mt-1">Sign in to your organization</p>
            </div>
            <div className="bg-white rounded-2xl border shadow-lg p-6 space-y-4">
              {tenantInfo.sso_enabled && (
                <div className="space-y-3">
                  <Button className="w-full gap-2 h-11 bg-gradient-to-r from-[#4F6DF5] to-[#6C5CE7]" onClick={() => {
                    const redirectUrl = window.location.origin + '/auth/callback';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}>
                    {tenantInfo.sso_provider === 'microsoft' && '🏢'}
                    {tenantInfo.sso_provider === 'okta' && '🔐'}
                    {tenantInfo.sso_provider === 'google_workspace' && '🔵'}
                    {!['microsoft','okta','google_workspace'].includes(tenantInfo.sso_provider) && '🔑'}
                    {' '}Sign in with {tenantInfo.sso_provider === 'microsoft' ? 'Microsoft' : tenantInfo.sso_provider === 'okta' ? 'Okta' : tenantInfo.sso_provider === 'google_workspace' ? 'Google' : 'SSO'}
                  </Button>
                  {!tenantInfo.enforce_sso && <div className="relative my-2"><div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-muted-foreground">or use email</span></div></div>}
                </div>
              )}
              {!tenantInfo.enforce_sso && (
                <Login onLogin={(t, r) => { handleLogin(t, r); window.history.replaceState({}, '', '/'); }} embedded />
              )}
              {tenantInfo.enforce_sso && !tenantInfo.sso_enabled && (
                <p className="text-sm text-center text-muted-foreground py-4">SSO is required but not yet configured. Contact your administrator.</p>
              )}
            </div>
            <p className="text-center text-[10px] text-muted-foreground mt-6">Powered by <button onClick={goToLanding} className="text-[#4F6DF5] hover:underline">LodgiFlow</button></p>
          </div>
        </div>
        <Toaster position="bottom-right" richColors closeButton />
      </>
    );
  }
  if (page === 'vendor-portal') {
    return (<><VendorPortalV2 /><Toaster position="bottom-right" richColors closeButton /></>);
  }
  if (page === 'brand-portal') {
    return (<><BrandPortalApp /><Toaster position="bottom-right" richColors closeButton /></>);
  }
  if (page === 'mgmt-portal') {
    return (<><MgmtPortalApp /><Toaster position="bottom-right" richColors closeButton /></>);
  }
  if (page === 'blog') {
    return (
      <>
        <div className="min-h-screen bg-white" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#E5E5E5]">
            <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
              <button onClick={goToLanding} className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>LodgiFlow</button>
              <button onClick={goToLanding} className="text-sm text-[#737373] hover:text-[#0A0A0A]">← Back</button>
            </div>
          </header>
          <BlogPage onNavigate={(page) => { if (page === 'signup') setPage('signup'); else setPage(page); }} />
        </div>
        <Toaster position="bottom-right" richColors closeButton />
      </>
    );
  }
  if (page === 'support') {
    const parts = window.location.pathname.split('/');
    const propId = parts[2] || '';
    const sToken = parts[3] || '';
    return (<><SupportPage propertyId={propId} token={sToken} /><Toaster position="bottom-right" richColors closeButton /></>);
  }
  if (page === 'signup') {
    return (<><SignupFlow onLogin={(t, r) => { setToken(t); setRole(r); setPage('app'); }} onGoToSite={goToLanding} /><Toaster position="bottom-right" richColors closeButton /></>);
  }

  // Marketing V2 variants — hero + first fold experiments
  if (page === 'v2a') {
    return (<><LandingV2A onGoToLogin={() => setPage('login')} onGoToSignup={() => setPage('signup')} /><VariantSwitcher /><Toaster position="bottom-right" richColors closeButton /></>);
  }
  if (page === 'v2b') {
    return (<><LandingV2B onGoToLogin={() => setPage('login')} onGoToSignup={() => setPage('signup')} /><VariantSwitcher /><Toaster position="bottom-right" richColors closeButton /></>);
  }
  if (page === 'v2c') {
    return (<><LandingV2C onGoToLogin={() => setPage('login')} onGoToSignup={() => setPage('signup')} /><VariantSwitcher /><Toaster position="bottom-right" richColors closeButton /></>);
  }
  if (page === 'v2d') {
    return (<><LandingV2D onGoToLogin={() => setPage('login')} onGoToSignup={() => setPage('signup')} /><VariantSwitcher /><Toaster position="bottom-right" richColors closeButton /></>);
  }
  if (page === 'tour') {
    return (<><StandaloneTour /><Toaster position="bottom-right" richColors closeButton /></>);
  }


  // Authenticated — show app
  if (token && page === 'app') {
    return (
      <>
        <Dashboard token={token} role={role} onLogout={handleLogout} onGoToSite={() => setPage('landing')} darkMode={darkMode} onToggleDarkMode={() => setDarkMode(d => !d)} />
        <Toaster position="bottom-right" richColors closeButton />
      </>
    );
  }

  // Login page
  if (page === 'login') {
    return (
      <>
        <Login onLogin={handleLogin} onBack={goToLanding} />
        <Toaster position="bottom-right" richColors closeButton />
      </>
    );
  }

  // Default — landing page
  return (
    <>
      <LandingPage
        onGoToLogin={() => setPage('login')}
        onGoToSignup={() => setPage('signup')}
        onGoToTerms={() => { window.history.pushState({}, '', '/terms'); setPage('terms'); }}
        onGoToPrivacy={() => { window.history.pushState({}, '', '/privacy'); setPage('privacy'); }}
      />
      <VariantSwitcher />
      <Toaster position="bottom-right" richColors closeButton />
      {swUpdateAvailable && (
        <div style={{position:'fixed',bottom:'70px',left:'16px',right:'16px',zIndex:9999,padding:'12px 16px',borderRadius:'12px',background:'#7c3aed',color:'white',fontSize:'13px',fontWeight:600,display:'flex',alignItems:'center',gap:'8px',boxShadow:'0 4px 20px rgba(0,0,0,0.3)',fontFamily:'system-ui,sans-serif'}} data-testid="sw-update-banner">
          <span style={{flex:1}}>New version available!</span>
          <button onClick={() => {
            if ('serviceWorker' in navigator) {
              navigator.serviceWorker.getRegistration().then(reg => {
                if (reg && reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
              });
            }
            setTimeout(() => window.location.reload(), 300);
          }} style={{background:'white',color:'#7c3aed',border:'none',padding:'6px 16px',borderRadius:'8px',fontWeight:700,fontSize:'12px',cursor:'pointer'}}>Update Now</button>
          <button onClick={() => setSwUpdateAvailable(false)} style={{background:'none',border:'none',color:'white',opacity:0.6,cursor:'pointer',fontSize:'16px'}}>&times;</button>
        </div>
      )}
    </>
  );
}

function AppWithI18n() {
  return (
    <I18nProvider>
      <RowDensityProvider>
        <FocusModeProvider>
          <App />
        </FocusModeProvider>
      </RowDensityProvider>
    </I18nProvider>
  );
}

export default AppWithI18n;
